/**
 * Calcule l'exposition de chaque bloc aux votes plats.
 * Source de vérité = profile.blockPreferences.dishLikes / dishDislikes.
 *
 * Pour chaque dish voté, chaque bloc présent (dish.blocks[].blockId) gagne
 * +1 like ou +1 dislike selon le sens du vote sur le plat.
 *
 * Note : un même blockId peut apparaître plusieurs fois dans dish.blocks
 * (rare, mais on ne le compte qu'une fois par dish pour ne pas biaiser).
 */

function blockIdsOf(dish) {
  if (!dish || !Array.isArray(dish.blocks)) return new Set();
  return new Set(dish.blocks.map((b) => b?.blockId).filter(Boolean));
}

/**
 * Calcule l'exposure de chaque bloc avec un blâme dilué :
 * un plat à K blocs voté donne 1/sqrt(K) à chaque bloc plutôt que 1.
 * Évite que tous les blocs accompagnement d'un plat détesté héritent
 * d'un dislike fort alors qu'un seul bloc est responsable.
 *
 * sqrt(K) (au lieu de K) garde du signal pour les blocs récurrents :
 *   K=1 → 1.0 (vote dédié)
 *   K=4 → 0.5
 *   K=8 → 0.35
 *
 * @param {string[]} dishLikeIds
 * @param {string[]} dishDislikeIds
 * @param {Array<{ id, blocks?: Array<{ blockId }> }>} dishCatalogue
 * @returns {Map<string, { likes: number, dislikes: number }>}
 */
export function computeBlockExposure(dishLikeIds, dishDislikeIds, dishCatalogue) {
  const exposure = new Map();
  const dishById = new Map((dishCatalogue || []).map((d) => [d.id, d]));

  function bump(ids, key) {
    for (const id of ids || []) {
      const dish = dishById.get(id);
      if (!dish) continue;
      const blockIds = Array.from(blockIdsOf(dish));
      if (blockIds.length === 0) continue;
      const weight = 1 / Math.sqrt(blockIds.length);
      for (const blockId of blockIds) {
        const cur = exposure.get(blockId) || { likes: 0, dislikes: 0 };
        cur[key] += weight;
        exposure.set(blockId, cur);
      }
    }
  }

  bump(dishLikeIds, 'likes');
  bump(dishDislikeIds, 'dislikes');
  return exposure;
}

/**
 * Variante objet plain pour sérialisation/Pinia.
 */
export function computeBlockExposureObject(dishLikeIds, dishDislikeIds, dishCatalogue) {
  const map = computeBlockExposure(dishLikeIds, dishDislikeIds, dishCatalogue);
  const out = {};
  for (const [k, v] of map) out[k] = v;
  return out;
}
